import { StateCreator } from 'zustand';

import {
  addFavoriteGame as fetchAddFavoriteGame,
  deleteFavoriteGame as fetchDeleteFavoriteGame,
  favoriteGames as fetchFavoriteGamesRequest,
  favoriteGamesIds as requestFavoriteGamesIds,
  gamesFilter as fetchGamesFilter,
  gamesFilterSelect as fetchGamesFilterSelect,
  providers as fetchProviders,
  Real,
} from '@/api/games';
import { CurrencyCode } from '@/helpers/currency';
import { createGamesListName, mapGameV2ToGameV1 } from '@/helpers/game';
import { DEMO_CURRENCY } from '@/helpers/variables';
import { CommonStoreType, GamesState } from '@/store/commonStore/types';

export const useGameSlice: StateCreator<CommonStoreType, [], [], GamesState> = (
  set,
  getState
) => {
  return {
    providers: [],
    pendingProviders: false,

    gameThemes: [],

    recentGames: [],
    pendingRecentGames: false,

    pendingFavoriteGames: false,

    favoriteGames: [],
    favoriteGamesIds: [],

    gamesByCategories: {},
    pendingGamesByCategories: {},

    gamesByProviders: {},
    pendingGamesByProviders: {},

    isLiveSpinsAvailable: false,

    fetchGames: async (body, onError) => {
      try {
        const { isMobileDevice, wallets, isAuthenticated } = getState();

        const accounts = wallets.map(
          ({ currency }) => currency
        ) as CurrencyCode[];

        const res = await fetchGamesFilter({
          device: isMobileDevice ? 'mobile' : 'desktop',
          currencies: isAuthenticated ? accounts : [DEMO_CURRENCY],
          ...body,
        });

        if (res.error) {
          onError &&
            onError({
              message: res.error,
              status: res.status,
              statusText: res.statusText,
            });
        }
        if (res.data) return res.data;
      } catch (error) {
        console.log(error);
      }
    },
    fetchGamesByIds: async (body, isMobileDevice) => {
      try {
        const { data } = await fetchGamesFilterSelect({
          device: isMobileDevice ? 'mobile' : 'desktop',
          ...body,
        });

        return data;
      } catch (error) {
        console.log(error);
      }
    },
    fetchFavoriteGames: async () => {
      try {
        set({ pendingFavoriteGames: true });
        const { data: favoriteGamesV2 } = await fetchFavoriteGamesRequest();
        const { data: favoriteGamesIds } = await requestFavoriteGamesIds();

        if (!favoriteGamesV2 || !favoriteGamesIds) return;

        const favoriteGames = mapGameV2ToGameV1(favoriteGamesV2 || []);

        set({ favoriteGames, favoriteGamesIds, pendingFavoriteGames: false });
      } catch (error) {
        set({ pendingFavoriteGames: false });
      }
    },
    addFavoriteGame: async (gameCurrencies: Real) => {
      const { user, fetchFavoriteGames } = getState();

      if (user?.currency == null) return;

      const currencyCode = gameCurrencies[user.currency].id;

      try {
        await fetchAddFavoriteGame(currencyCode);
        await fetchFavoriteGames();
      } catch (error) {
        console.log(error);
      }
    },
    removeFavoriteGame: async (gameCurrencies: Real) => {
      const { user, fetchFavoriteGames, favoriteGamesIds } = getState();

      if (user?.currency == null) return;

      const favoriteGameId = Object.values(gameCurrencies)
        .map(({ id }) => id)
        .find((currency) => favoriteGamesIds.includes(currency));

      if (!favoriteGameId) return;

      try {
        await fetchDeleteFavoriteGame(favoriteGameId);
        await fetchFavoriteGames();
      } catch (error) {
        console.log(error);
      }
    },
    fetchRecentGames: async (isMobileDevice: boolean) => {
      try {
        const { isAuthenticated, recentGamesIds, fetchGamesByIds } = getState();

        if (!isAuthenticated) return;

        set({ pendingRecentGames: true });

        const recentGames = await fetchGamesByIds(
          {
            gameIds: recentGamesIds,
          },
          isMobileDevice
        );

        if (!recentGames) return;

        const games = mapGameV2ToGameV1(Object.values(recentGames) || []);

        set({ recentGames: games, pendingRecentGames: false });
      } catch (e) {
        console.log(e);
      }
    },
    addGamesByCategory: (gamesInfo) => {
      const { gamesByCategories } = getState();

      set({
        gamesByCategories: { ...gamesByCategories, ...gamesInfo },
      });
    },
    togglePendingGamesByCategories: (category) => {
      const { pendingGamesByCategories } = getState();

      set({
        pendingGamesByCategories: {
          ...pendingGamesByCategories,
          [category]: !pendingGamesByCategories[category],
        },
      });
    },
    getGamesByCategory: async (
      category,
      { pageSize = 100, page = 1, staticAmount = false, subName }
    ) => {
      try {
        const {
          gamesByCategories,
          pendingGamesByCategories,
          fetchGames,
          addGamesByCategory,
          togglePendingGamesByCategories,
        } = getState();

        const listName = createGamesListName(category, subName);
        const gamesByCategoryList = gamesByCategories[listName];

        if (
          staticAmount &&
          (gamesByCategoryList?.games?.length >= pageSize ||
            pendingGamesByCategories[listName])
        ) {
          return gamesByCategoryList;
        }

        togglePendingGamesByCategories(listName);

        const games = await fetchGames({
          filter: { categories: { identifiers: [category] } },
          pageSize,
          page,
        });

        togglePendingGamesByCategories(listName);

        if (!games?.data) return;

        const gamesV1 = mapGameV2ToGameV1(games.data || []);

        const categoryGames = {
          games:
            page === 1
              ? gamesV1
              : [...(gamesByCategoryList?.games || []), ...gamesV1],
          pagination: games?.pagination || {},
        };

        addGamesByCategory({
          [listName]: categoryGames,
        });

        return categoryGames;
      } catch (e) {
        console.log(e);
      }
    },

    fetchProviders: async () => {
      try {
        set({ pendingProviders: true });
        const { data } = await fetchProviders();

        set({ providers: data || [], pendingProviders: false });
      } catch (e) {
        console.log(e);
        set({ pendingProviders: false });
      }
    },

    addGamesByProvider: (gamesInfo) => {
      const { gamesByProviders } = getState();

      set({
        gamesByProviders: { ...gamesByProviders, ...gamesInfo },
      });
    },
    togglePendingGamesByProviders: (provider) => {
      const { pendingGamesByProviders } = getState();

      set({
        pendingGamesByProviders: {
          ...pendingGamesByProviders,
          [provider]: !pendingGamesByProviders[provider],
        },
      });
    },
    getGamesByProvider: async (
      provider,
      { pageSize = 100, page = 1, staticAmount = false, subName, sort }
    ) => {
      try {
        const {
          fetchGames,
          gamesByProviders,
          pendingGamesByProviders,
          togglePendingGamesByProviders,
          addGamesByProvider,
        } = getState();

        const listName = createGamesListName(provider, subName);
        const gamesByProviderList = gamesByProviders[listName];

        if (
          staticAmount &&
          (gamesByProviderList?.games?.length >= pageSize ||
            pendingGamesByProviders[listName])
        ) {
          return gamesByProviderList;
        }

        togglePendingGamesByProviders(listName);

        const games = await fetchGames({
          filter: { providers: [provider] },
          sort,
          pageSize,
          page,
        });

        togglePendingGamesByProviders(listName);

        if (!games?.data) return;

        const gamesV1 = mapGameV2ToGameV1(games.data || []);

        const providerGames = {
          games:
            page === 1
              ? gamesV1
              : [...(gamesByProviderList?.games || []), ...gamesV1],
          pagination: games?.pagination || {},
        };

        addGamesByProvider({
          [listName]: providerGames,
        });

        return providerGames;
      } catch (e) {
        console.log(e);
      }
    },
  };
};
